"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState, useMemo } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import { generateUniqueKeys, mapToExtendedDrops } from "@/helpers/waves/wave-drops.helpers";
import { useDebounce } from "react-use";
import { getDefaultQueryRetry, WAVE_DROPS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDropsLeaderboardPage } from "@/generated/models/ApiDropsLeaderboardPage";
import useCapacitor from "./useCapacitor";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
export enum WaveDropsLeaderboardSort {
  RANK = "RANK",
  RATING_PREDICTION = "RATING_PREDICTION",
  MY_REALTIME_VOTE = "MY_REALTIME_VOTE",
  CREATED_AT = "CREATED_AT",
}
interface UseWaveDropsLeaderboardProps {
  readonly waveId: string;
  readonly connectedProfileHandle: string | null;
  readonly sort?: WaveDropsLeaderboardSort | undefined;
  readonly pausePolling?: boolean | undefined;
}
const SORT_DIRECTION_MAP: Record<WaveDropsLeaderboardSort, string | undefined> = {
  [WaveDropsLeaderboardSort.RANK]: undefined,
  [WaveDropsLeaderboardSort.RATING_PREDICTION]: "DESC",
  [WaveDropsLeaderboardSort.MY_REALTIME_VOTE]: undefined,
  [WaveDropsLeaderboardSort.CREATED_AT]: "DESC"
};
const POLLING_DELAY = 3000;
const ACTIVE_POLLING_INTERVAL = 5000;
const INACTIVE_POLLING_INTERVAL = 30000;
function useTabVisibility() {
  const $ = _c(2);
  const [isVisible, setIsVisible] = useState(!document.hidden);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleVisibilityChange = () => setIsVisible(!document.hidden);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isVisible;
}
export function useWaveDropsLeaderboard(t0) {
  const $ = _c(90);
  const {
    waveId,
    connectedProfileHandle,
    sort: t1,
    pausePolling: t2
  } = t0;
  const sort = t1 === undefined ? WaveDropsLeaderboardSort.RANK : t1;
  const pausePolling = t2 === undefined ? false : t2;
  const {
    isCapacitor
  } = useCapacitor();
  const queryClient = useQueryClient();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [drops, setDrops] = useState(t3);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [haveNewDrops, setHaveNewDrops] = useState(false);
  const [canPoll, setCanPoll] = useState(false);
  const [delayedPollingResult, setDelayedPollingResult] = useState(undefined);
  const isTabVisible = useTabVisibility();
  const [currentSort, setCurrentSort] = useState(sort);
  const isSortChanging = currentSort !== sort;
  let t4;
  let t5;
  if ($[1] !== currentSort || $[2] !== sort) {
    t4 = () => {
      if (currentSort !== sort) {
        setCurrentSort(sort);
        setDrops([]);
        setHasInitialized(false);
      }
    };
    t5 = [sort, currentSort];
    $[1] = currentSort;
    $[2] = sort;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  const sortDirection = SORT_DIRECTION_MAP[sort];
  let t6;
  if ($[5] !== sort || $[6] !== sortDirection || $[7] !== waveId) {
    t6 = [QueryKey.DROPS_LEADERBOARD, {
      waveId,
      page_size: WAVE_DROPS_PARAMS.limit,
      sort,
      sort_direction: sortDirection
    }];
    $[5] = sort;
    $[6] = sortDirection;
    $[7] = waveId;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const queryKey = t6;
  let t7;
  if ($[9] !== sort) {
    t7 = lastPage => {
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        const haveZeroVotes = lastPage.drops.some(_temp);
        if (haveZeroVotes) {
          return null;
        }
      }
      return lastPage.next ? lastPage.page + 1 : null;
    };
    $[9] = sort;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const getNextPageParam = t7;
  let t8;
  if ($[11] !== getNextPageParam || $[12] !== queryClient || $[13] !== queryKey || $[14] !== sort || $[15] !== sortDirection || $[16] !== waveId) {
    t8 = () => {
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t9 => {
          const {
            pageParam
          } = t9;
          const params = {
            page_size: WAVE_DROPS_PARAMS.limit.toString(),
            sort
          };
          if (sortDirection) {
            params.sort_direction = sortDirection;
          }
          if (pageParam) {
            params.page = `${pageParam}`;
          }
          return await commonApiFetch({
            endpoint: `waves/${waveId}/leaderboard`,
            params
          });
        },
        initialPageParam: null,
        getNextPageParam,
        pages: 1,
        staleTime: 60000,
        ...getDefaultQueryRetry()
      });
    };
    $[11] = getNextPageParam;
    $[12] = queryClient;
    $[13] = queryKey;
    $[14] = sort;
    $[15] = sortDirection;
    $[16] = waveId;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== sort || $[19] !== waveId) {
    t9 = [waveId, sort];
    $[18] = sort;
    $[19] = waveId;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  if ($[21] !== sort || $[22] !== sortDirection || $[23] !== waveId) {
    t10 = async t11 => {
      const {
        pageParam: pageParam_0
      } = t11;
      const params_0 = {
        page_size: WAVE_DROPS_PARAMS.limit.toString(),
        sort
      };
      if (sortDirection) {
        params_0.sort_direction = sortDirection;
      }
      if (pageParam_0) {
        params_0.page = `${pageParam_0}`;
      }
      const results = await commonApiFetch({
        endpoint: `waves/${waveId}/leaderboard`,
        params: params_0
      });
      return results;
    };
    $[21] = sort;
    $[22] = sortDirection;
    $[23] = waveId;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = !!connectedProfileHandle && !pausePolling;
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = getDefaultQueryRetry();
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== getNextPageParam || $[27] !== queryKey || $[28] !== t10 || $[29] !== t11) {
    t13 = {
      queryKey,
      queryFn: t10,
      initialPageParam: null,
      getNextPageParam,
      enabled: t11,
      staleTime: 60000,
      ...t12
    };
    $[26] = getNextPageParam;
    $[27] = queryKey;
    $[28] = t10;
    $[29] = t11;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t13);
  let t14;
  if ($[31] !== data || $[32] !== sort) {
    bb0: {
      if (!data?.pages) {
        let t15;
        if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
          t15 = [];
          $[34] = t15;
        } else {
          t15 = $[34];
        }
        t14 = t15;
        break bb0;
      }
      const mappedDrops = mapToExtendedDrops(data.pages.map(_temp2), []);
      const uniqueDrops = generateUniqueKeys(mappedDrops, []);
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        t14 = uniqueDrops.filter(_temp3);
        break bb0;
      }
      t14 = uniqueDrops;
    }
    $[31] = data;
    $[32] = sort;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const processedDrops = t14;
  let t15;
  if ($[35] !== data?.pages || $[36] !== processedDrops) {
    t15 = () => {
      if (!data?.pages) {
        return;
      }
      setDrops(processedDrops);
      setHasInitialized(true);
    };
    $[35] = data?.pages;
    $[36] = processedDrops;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== data || $[39] !== processedDrops) {
    t16 = [processedDrops, data];
    $[38] = data;
    $[39] = processedDrops;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  useEffect(t15, t16);
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setCanPoll(true);
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== data) {
    t18 = [data];
    $[42] = data;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  useDebounce(t17, 10000, t18);
  let t19;
  if ($[44] !== queryKey) {
    t19 = [...queryKey, "polling"];
    $[44] = queryKey;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== sort || $[47] !== sortDirection || $[48] !== waveId) {
    t20 = async () => {
      const params_1 = {
        page_size: "1",
        sort
      };
      if (sortDirection) {
        params_1.sort_direction = sortDirection;
      }
      return await commonApiFetch({
        endpoint: `waves/${waveId}/leaderboard`,
        params: params_1
      });
    };
    $[46] = sort;
    $[47] = sortDirection;
    $[48] = waveId;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const t21 = !haveNewDrops && canPoll && !pausePolling;
  const t22 = isTabVisible ? ACTIVE_POLLING_INTERVAL : INACTIVE_POLLING_INTERVAL;
  const t23 = !isCapacitor;
  let t24;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = getDefaultQueryRetry();
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t19 || $[52] !== t20 || $[53] !== t21 || $[54] !== t22 || $[55] !== t23) {
    t25 = {
      queryKey: t19,
      queryFn: t20,
      enabled: t21,
      refetchInterval: t22,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: t23,
      ...t24
    };
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
    $[54] = t22;
    $[55] = t23;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  const {
    data: pollingResult
  } = useQuery(t25);
  let t26;
  let t27;
  if ($[57] !== pausePolling || $[58] !== pollingResult) {
    t26 = () => {
      if (pollingResult && !pausePolling) {
        const timer = setTimeout(() => {
          setDelayedPollingResult(pollingResult);
        }, POLLING_DELAY);
        return () => clearTimeout(timer);
      }
    };
    t27 = [pollingResult, pausePolling];
    $[57] = pausePolling;
    $[58] = pollingResult;
    $[59] = t26;
    $[60] = t27;
  } else {
    t26 = $[59];
    t27 = $[60];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[61] !== delayedPollingResult || $[62] !== drops) {
    t28 = () => {
      if (delayedPollingResult !== undefined) {
        if (delayedPollingResult.drops.length > 0) {
          const latestPolledDrop = delayedPollingResult.drops[0];
          if (drops.length > 0) {
            const latestExistingDrop = drops.at(-1);
            const polledCreatedAt = new Date(latestPolledDrop?.created_at).getTime();
            const existingCreatedAt = new Date(latestExistingDrop?.created_at ?? 0).getTime();
            setHaveNewDrops(polledCreatedAt > existingCreatedAt);
          } else {
            setHaveNewDrops(true);
          }
        } else {
          setHaveNewDrops(false);
        }
      }
    };
    t29 = [delayedPollingResult, drops];
    $[61] = delayedPollingResult;
    $[62] = drops;
    $[63] = t28;
    $[64] = t29;
  } else {
    t28 = $[63];
    t29 = $[64];
  }
  useEffect(t28, t29);
  let t30;
  if ($[65] !== drops || $[66] !== haveNewDrops || $[67] !== isTabVisible || $[68] !== refetch) {
    t30 = () => {
      if (!haveNewDrops) {
        return;
      }
      if (!isTabVisible) {
        return;
      }
      const hasTempDrop = drops.some(_temp4);
      if (hasTempDrop) {
        return;
      }
      refetch();
      setHaveNewDrops(false);
    };
    $[65] = drops;
    $[66] = haveNewDrops;
    $[67] = isTabVisible;
    $[68] = refetch;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== drops || $[71] !== haveNewDrops || $[72] !== isTabVisible) {
    t31 = [haveNewDrops, isTabVisible, drops];
    $[70] = drops;
    $[71] = haveNewDrops;
    $[72] = isTabVisible;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  useEffect(t30, t31);
  let t32;
  let t33;
  if ($[74] !== queryClient || $[75] !== waveId) {
    t32 = () => () => {
      queryClient.removeQueries({
        queryKey: [QueryKey.DROPS, {
          waveId
        }]
      });
    };
    t33 = [waveId, queryClient];
    $[74] = queryClient;
    $[75] = waveId;
    $[76] = t32;
    $[77] = t33;
  } else {
    t32 = $[76];
    t33 = $[77];
  }
  useEffect(t32, t33);
  let t34;
  if ($[78] !== fetchNextPage || $[79] !== hasNextPage) {
    t34 = async () => {
      if (hasNextPage) {
        await fetchNextPage();
      }
    };
    $[78] = fetchNextPage;
    $[79] = hasNextPage;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  const manualFetch = t34;
  const t35 = isFetching || !hasInitialized || isSortChanging;
  let t36;
  if ($[81] !== drops || $[82] !== fetchNextPage || $[83] !== hasNextPage || $[84] !== haveNewDrops || $[85] !== isFetchingNextPage || $[86] !== manualFetch || $[87] !== refetch || $[88] !== t35) {
    t36 = {
      drops,
      fetchNextPage,
      hasNextPage,
      isFetching: t35,
      isFetchingNextPage,
      refetch,
      haveNewDrops,
      manualFetch
    };
    $[81] = drops;
    $[82] = fetchNextPage;
    $[83] = hasNextPage;
    $[84] = haveNewDrops;
    $[85] = isFetchingNextPage;
    $[86] = manualFetch;
    $[87] = refetch;
    $[88] = t35;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  return t36;
}
function _temp4(drop_1) {
  return drop_1.id.startsWith("temp-");
}
function _temp3(drop_0) {
  return drop_0.context_profile_context?.rating !== 0;
}
function _temp2(page) {
  return {
    wave: page.wave,
    drops: page.drops
  };
}
function _temp(drop) {
  return drop.context_profile_context?.rating === 0;
}